import React from 'react';
import { useDealSaverInfo, useSalesReps, useUpdateScores } from './api';
import moment from 'moment';
import { useForm, FormProvider, useFormContext, Controller } from 'react-hook-form';
import InputMask from 'react-input-mask';

/**
 * @param {import('./api').CallType[]} data
 */
function createCallTypeStatusMap(data) {
  /**
   * @type {Map<
   *   number,
   *   {
   *     callType: string;
   *     dealsaverEnabled: boolean;
   *     processingQueueName: string;
   *     processingQueueId: number;
   *     callStatuses: { callStatusId: number; callStatusText: string }[];
   *   }
   * >}
   */
  const callTypeStatusMap = new Map();

  data.forEach((item) => {
    const callTypeId = item.call_type_id;
    const callType = item.call_type_text;
    const dealsaverEnabled = Boolean(item.dealsaver_enabled);
    const processingQueueName = item.processing_queue_name;
    const processingQueueId = item.processing_queue_type_id;
    const callStatus = { callStatusId: item.call_status_id, callStatusText: item.call_status_text };

    if (!callTypeStatusMap.has(callTypeId)) {
      callTypeStatusMap.set(callTypeId, {
        callType,
        dealsaverEnabled,
        processingQueueName,
        processingQueueId,
        callStatuses: [callStatus],
      });
    } else {
      const existingStatuses = callTypeStatusMap.get(callTypeId)?.callStatuses;
      if (!existingStatuses?.some((status) => status.callStatusId === callStatus.callStatusId)) {
        existingStatuses?.push(callStatus);
      }
    }
  });

  return callTypeStatusMap;
}

/**
 * @param {{
 *   callTypes: import('./api').CallType[];
 *   scoringSettings: import('./api').ScoringSettings;
 *   onSaveSuccess?: () => void;
 *   callId: string;
 * }} props
 */
export default function RescoreForm({ callTypes, scoringSettings, onSaveSuccess, callId }) {
  const formMethods = useForm({
    defaultValues: {
      ...scoringSettings,
      appointment_date: moment(scoringSettings.appointment_date).format('YYYY-MM-DD'),
      appointment_time: moment(scoringSettings.appointment_date).format('HH:mm'),
    },
  });

  const updateScoreMutation = useUpdateScores(callId);

  function onSubmit(_data, event) {
    // We get the form values from the event rather than the data from react hook form since
    // we don't want to include the values for the hidden fields in the form
    const formData = new FormData(event.target);
    const formValues = Object.fromEntries(formData.entries());

    // If a value is an empty string, set it to null
    Object.keys(formValues).forEach((key) => {
      if (formValues[key] === '') {
        formValues[key] = null;
      }
    });

    // Unmask the phone number value
    if (typeof formValues.caller_alt_phone === 'string') {
      formValues.caller_alt_phone = formValues.caller_alt_phone.replace(/\D/g, '');
    }

    // Mutate appointment date to be a datetime string in iso format
    if (formValues.appointment_date && formValues.appointment_time) {
      formValues.appointment_date = moment(
        `${formValues.appointment_date} ${formValues.appointment_time}`,
        'YYYY-MM-DD HH:mm'
      ).toISOString();
    }

    // Include all values from the scoring settings query, override with the visible form values,
    // and include the callid
    const variables = {
      ...scoringSettings,
      ...formValues,
      call_id: callId,
    };
    updateScoreMutation.mutate(variables, { onSuccess: onSaveSuccess });
  }

  const callTypeStatusMap = createCallTypeStatusMap(callTypes);
  const selectedCallTypeId = formMethods.watch('call_type_id');
  const selectedCallType = callTypeStatusMap.get(Number(selectedCallTypeId));
  const isDealsaverEnabled = Boolean(selectedCallType?.dealsaverEnabled);
  const queueName = selectedCallType?.processingQueueName;
  const queueId = selectedCallType?.processingQueueId;
  const dealSaverInfoQuery = useDealSaverInfo(queueId);

  const salesRepQuery = useSalesReps(scoringSettings.domain_id);

  // Call types with no statuses have a single status with an empty string as the text
  const shouldShowCallStatus = !(
    selectedCallType?.callStatuses.length === 1 &&
    selectedCallType?.callStatuses[0].callStatusText === ''
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <div
          style={{
            paddingBottom: 15,
            borderBottom: '1px solid lightgray',
          }}
        >
          <h4>LeadScore Options</h4>
          <div className='row'>
            <div className='col-xs-6'>
              <div
                className={`form-group ${
                  formMethods.formState.errors.call_type_id ? 'has-error' : ''
                }`}
                style={{ marginBottom: 15 }}
              >
                <label htmlFor='call_type_id'>Call Type</label>
                <select
                  id='call_type_id'
                  {...formMethods.register('call_type_id', {
                    required: true,
                    // Clear the call status when the call type changes since the call status is
                    // dependent on the call type
                    onChange() {
                      formMethods.setValue('call_status_id', '');
                    },
                  })}
                  className='form-control'
                >
                  <option value=''>Select a call type...</option>
                  {[...callTypeStatusMap.entries()].map(([callTypeId, value]) => {
                    return (
                      <option key={callTypeId} value={callTypeId}>
                        {value.callType}
                      </option>
                    );
                  })}
                </select>
                {formMethods.formState.errors.call_type_id ? (
                  <span className='help-block'>Call type is required</span>
                ) : null}
              </div>
            </div>
            <div className='col-xs-6'>
              {shouldShowCallStatus ? (
                <div
                  className={`form-group ${
                    formMethods.formState.errors.call_status_id ? 'has-error' : ''
                  }`}
                  style={{ marginBottom: 15 }}
                >
                  <label htmlFor='call_status_id'>Call Status</label>
                  <select
                    id='call_status_id'
                    {...formMethods.register('call_status_id', { required: true })}
                    className='form-control'
                  >
                    <option value=''>Select a call status...</option>
                    {selectedCallType?.callStatuses.map((callStatus) => (
                      <option key={callStatus.callStatusId} value={callStatus.callStatusId}>
                        {callStatus.callStatusText}
                      </option>
                    ))}
                  </select>
                  {formMethods.formState.errors.call_status_id ? (
                    <span className='help-block'>Call status is required</span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {isDealsaverEnabled ? (
          <div
            style={{
              paddingTop: 30,
              paddingBottom: 15,
              borderBottom: '1px solid lightgray',
            }}
          >
            <h4>DealSaver Options</h4>
            {salesRepQuery.isLoading || dealSaverInfoQuery.isLoading ? (
              <div>Loading...</div>
            ) : salesRepQuery.isError || dealSaverInfoQuery.isError ? (
              <div>Failed to fetch DealSaver Options</div>
            ) : (
              <div>
                {queueName === 'automotive' || queueName === 'spanish automotive' ? (
                  // caller name, initial contact, sales rep, department, call summary, email, phone,
                  // appointment set, appointment time, appointment offered, call alert
                  <AutomotiveDealsaverOptions
                    salesReps={salesRepQuery.data}
                    dealSaverInfo={dealSaverInfoQuery.data}
                  />
                ) : queueName === 'pest' || queueName === 'home improvement' ? (
                  // appointment set, call alert, sales rep
                  <PestDealsaverOptions
                    salesReps={salesRepQuery.data}
                    dealSaverInfo={dealSaverInfoQuery.data}
                  />
                ) : queueName === 'dslite - automotive' ? (
                  // sales rep, department, alt phone, appointment set, appointment time
                  <DSLiteOptions
                    salesReps={salesRepQuery.data}
                    dealSaverInfo={dealSaverInfoQuery.data}
                  />
                ) : queueName === 'dsexpress - automotive' ? (
                  // caller name, initial contact, sales rep, department, alt phone, appointment set,
                  // appointment time, appointment offered, call alert
                  <DSExpressOptions
                    salesReps={salesRepQuery.data}
                    dealSaverInfo={dealSaverInfoQuery.data}
                  />
                ) : (
                  // appointment set, appointment time, sales rep, department (fix because of bad requirements)
                  <HealthcareDealSaverOptions 
                    salesReps={salesRepQuery.data} 
                    dealSaverInfo={dealSaverInfoQuery.data}
                    />
                )}
              </div>
            )}
          </div>
        ) : null}
        <div
          style={{
            marginTop: 15,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            disabled={updateScoreMutation.isLoading}
            type='submit'
            className='btn btn-success'
          >
            Save Changes
          </button>
        </div>
      </form>
    </FormProvider>
  );
}

/**
 * @param {Object} props
 * @param {import('./api').SalesRep[]} props.salesReps
 */
function HealthcareDealSaverOptions({ salesReps, dealSaverInfo }) {
  const { register, watch, formState } = useFormContext();
  const appointmentSet = watch('appointment_set');

  return (
    <div className='row'>
      <div className='col-xs-6'>
        <div
          className={`form-group ${formState.errors.appointment_set ? 'has-error' : ''}`}
          style={{ marginBottom: 15 }}
        >
          <label className='control-label'>Appointment Set?</label>
          <div>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='1' />{' '}
              Yes
            </label>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='0' />{' '}
              No
            </label>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='-1' />{' '}
              N/A
            </label>
          </div>
          {formState.errors.appointment_set ? (
            <span className='help-block'>Appointment set is required</span>
          ) : null}
        </div>
        {appointmentSet === '1' ? (
          <>
            <div
              className={`form-group ${formState.errors.appointment_date ? 'has-error' : ''}`}
              style={{ marginBottom: 15 }}
            >
              <label htmlFor='appointment_date'>Date</label>
              <input
                className='form-control'
                type='date'
                id='appointment_date'
                {...register('appointment_date', { required: true })}
              />
              {formState.errors.appointment_date ? (
                <span className='help-block'>Appointment date is required</span>
              ) : null}
            </div>
            <div
              className={`form-group ${formState.errors.appointment_time ? 'has-error' : ''}`}
              style={{ marginBottom: 15 }}
            >
              <label htmlFor='appointment_time'>Time</label>
              <input
                className='form-control'
                type='time'
                id='appointment_time'
                {...register('appointment_time', { required: true })}
              />
              {formState.errors.appointment_time ? (
                <span className='help-block'>Appointment time is required</span>
              ) : null}
            </div>
          </>
        ) : null}
      </div>

      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='representative_user_id'>Sales Rep</label>
          <select
            className='form-control'
            id='representative_user_id'
            {...register('representative_user_id')}
          >
            <option value=''>Select</option>
            {salesReps.map((salesRep) => (
              <option key={salesRep.employee_id} value={salesRep.employee_id}>
                {salesRep.employee_name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {dealSaverInfo.departments.length > 0 ? (
        <div className='col-xs-6 col-lg-3'>
          <div className='form-group' style={{ marginBottom: 15 }}>
            <label htmlFor='department_id'>Department Called</label>
            <select id='department_id' {...register('department_id')} className='form-control'>
              <option value=''>Select</option>
              {dealSaverInfo.departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.text}
                </option>
              ))}
            </select>
          </div>
        </div>
      ): null}
    </div>
  );
}

/**
 * @param {{
 *   salesReps: import('./api').SalesRep[];
 *   dealSaverInfo: import('./api').DealSaverInfo;
 * }} props
 */
function PestDealsaverOptions({ salesReps, dealSaverInfo }) {
  const { register, formState } = useFormContext();

  return (
    <div className='row'>
      <div className='col-xs-4'>
        <div
          className={`form-group ${formState.errors.appointment_set ? 'has-error' : ''}`}
          style={{ marginBottom: 15 }}
        >
          <label className='control-label'>Appointment Set?</label>
          <div>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='1' />{' '}
              Yes
            </label>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='0' />{' '}
              No
            </label>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='-1' />{' '}
              N/A
            </label>
          </div>
          {formState.errors.appointment_set ? (
            <span className='help-block'>Appointment set is required</span>
          ) : null}
        </div>
      </div>
      <div className='col-xs-4'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='call_alert_id'>Call Alert</label>
          <select id='call_alert_id' {...register('call_alert_id')} className='form-control'>
            <option value=''>Select</option>
            {dealSaverInfo.call_alerts.map((callAlert) => (
              <option key={callAlert.id} value={callAlert.id}>
                {callAlert.text}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='col-xs-4'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='representative_user_id'>Sales Rep</label>
          <select
            className='form-control'
            id='representative_user_id'
            {...register('representative_user_id')}
          >
            <option value=''>Select</option>
            {salesReps.map((salesRep) => (
              <option key={salesRep.employee_id} value={salesRep.employee_id}>
                {salesRep.employee_name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   salesReps: import('./api').SalesRep[];
 *   dealSaverInfo: import('./api').DealSaverInfo;
 * }} props
 */
function DSExpressOptions({ salesReps, dealSaverInfo }) {
  const { register, watch, formState, control } = useFormContext();
  const appointmentSet = watch('appointment_set');

  return (
    <div className='row'>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='caller_name'>Caller Name</label>
          <input
            type='text'
            className='form-control'
            id='caller_name'
            {...register('caller_name')}
            placeholder="Caller's Name"
          />
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='initial_contact'>Initial Contact</label>
          <input
            type='text'
            className='form-control'
            id='initial_contact'
            {...register('initial_contact')}
            placeholder='Name of initial contact'
          />
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='representative_user_id'>Sales Rep</label>
          <select
            className='form-control'
            id='representative_user_id'
            {...register('representative_user_id')}
          >
            <option value=''>Select</option>
            {salesReps.map((salesRep) => (
              <option key={salesRep.employee_id} value={salesRep.employee_id}>
                {salesRep.employee_name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='department_id'>Department Called</label>
          <select id='department_id' {...register('department_id')} className='form-control'>
            <option value=''>Select</option>
            {dealSaverInfo.departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.text}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='col-xs-6 col-md-4'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='caller_alt_phone'>Alt Phone</label>
          <Controller
            name='caller_alt_phone'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <InputMask
                mask='(999) 999-9999'
                type='text'
                className='form-control mask_phone'
                id='caller_alt_phone'
                placeholder="Caller's Phone Number"
                {...field}
              />
            )}
          />
        </div>
      </div>
      <div className='col-xs-6 col-md-4'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='call_alert_id'>Call Alert</label>
          <select id='call_alert_id' {...register('call_alert_id')} className='form-control'>
            <option value=''>Select</option>
            {dealSaverInfo.call_alerts.map((callAlert) => (
              <option key={callAlert.id} value={callAlert.id}>
                {callAlert.text}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='col-xs-6 col-md-4'>
        <div
          className={`form-group ${formState.errors.appointment_set ? 'has-error' : ''}`}
          style={{ marginBottom: 15 }}
        >
          <label className='control-label'>Appointment Set?</label>
          <div>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='1' />{' '}
              Yes
            </label>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='0' />{' '}
              No
            </label>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='-1' />{' '}
              N/A
            </label>
          </div>
          {formState.errors.appointment_set ? (
            <span className='help-block'>Appointment set is required</span>
          ) : null}
        </div>
        {appointmentSet === '1' ? (
          <>
            <div
              className={`form-group ${formState.errors.appointment_date ? 'has-error' : ''}`}
              style={{ marginBottom: 15 }}
            >
              <label htmlFor='appointment_date'>Date</label>
              <input
                className='form-control'
                type='date'
                id='appointment_date'
                {...register('appointment_date', { required: true })}
              />
              {formState.errors.appointment_date ? (
                <span className='help-block'>Appointment date is required</span>
              ) : null}
            </div>
            <div
              className={`form-group ${formState.errors.appointment_time ? 'has-error' : ''}`}
              style={{ marginBottom: 15 }}
            >
              <label htmlFor='appointment_time'>Time</label>
              <input
                className='form-control'
                type='time'
                id='appointment_time'
                {...register('appointment_time', { required: true })}
              />
              {formState.errors.appointment_time ? (
                <span className='help-block'>Appointment time is required</span>
              ) : null}
            </div>
          </>
        ) : null}
        {appointmentSet !== '-1' ? (
          <>
            <div
              className={`form-group ${formState.errors.appointment_offered ? 'has-error' : ''}`}
              style={{ marginBottom: 15 }}
            >
              <label className='control-label'>Appointment Offered</label>
              <div>
                <label className='radio-inline'>
                  <input
                    type='radio'
                    {...register('appointment_offered', { required: true })}
                    value='1'
                  />{' '}
                  Yes
                </label>
                <label className='radio-inline'>
                  <input
                    type='radio'
                    {...register('appointment_offered', { required: true })}
                    value='0'
                  />{' '}
                  No
                </label>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * @param {{
 *   salesReps: import('./api').SalesRep[];
 *   dealSaverInfo: import('./api').DealSaverInfo;
 * }} props
 */
function DSLiteOptions({ salesReps, dealSaverInfo }) {
  const { register, watch, formState, control } = useFormContext();
  const appointmentSet = watch('appointment_set');

  return (
    <div className='row'>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='representative_user_id'>Sales Rep</label>
          <select
            className='form-control'
            id='representative_user_id'
            {...register('representative_user_id')}
          >
            <option value=''>Select</option>
            {salesReps.map((salesRep) => (
              <option key={salesRep.employee_id} value={salesRep.employee_id}>
                {salesRep.employee_name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='department_id'>Department Called</label>
          <select id='department_id' {...register('department_id')} className='form-control'>
            <option value=''>Select</option>
            {dealSaverInfo.departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.text}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='caller_alt_phone' className='col-xs-2 control-label'>
            Phone
          </label>
          <div className='col-xs-10'>
            <Controller
              name='caller_alt_phone'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <InputMask
                  mask='(999) 999-9999'
                  type='text'
                  className='form-control mask_phone'
                  id='caller_alt_phone'
                  placeholder="Caller's Phone Number"
                  {...field}
                />
              )}
            />
          </div>
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div
          className={`form-group ${formState.errors.appointment_set ? 'has-error' : ''}`}
          style={{ marginBottom: 15 }}
        >
          <label className='control-label'>Appointment Set?</label>
          <div>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='1' />{' '}
              Yes
            </label>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='0' />{' '}
              No
            </label>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='-1' />{' '}
              N/A
            </label>
          </div>
          {formState.errors.appointment_set ? (
            <span className='help-block'>Appointment set is required</span>
          ) : null}
        </div>
        {appointmentSet === 1 ? (
          <>
            <div
              className={`form-group ${formState.errors.appointment_date ? 'has-error' : ''}`}
              style={{ marginBottom: 15 }}
            >
              <label htmlFor='appointment_date'>Date</label>
              <input
                className='form-control'
                type='date'
                id='appointment_date'
                {...register('appointment_date', { required: true })}
              />
              {formState.errors.appointment_date ? (
                <span className='help-block'>Appointment date is required</span>
              ) : null}
            </div>
            <div
              className={`form-group ${formState.errors.appointment_time ? 'has-error' : ''}`}
              style={{ marginBottom: 15 }}
            >
              <label htmlFor='appointment_time'>Time</label>
              <input
                className='form-control'
                type='time'
                id='appointment_time'
                {...register('appointment_time', { required: true })}
              />
              {formState.errors.appointment_time ? (
                <span className='help-block'>Appointment time is required</span>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * @param {{
 *   salesReps: import('./api').SalesRep[];
 *   dealSaverInfo: import('./api').DealSaverInfo;
 * }} props
 */
function AutomotiveDealsaverOptions({ salesReps, dealSaverInfo }) {
  const { register, watch, formState, control } = useFormContext();
  const appointmentSet = watch('appointment_set');

  return (
    <div className='row'>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='caller_name'>Caller Name</label>
          <input
            type='text'
            className='form-control'
            id='caller_name'
            {...register('caller_name')}
            placeholder="Caller's Name"
          />
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='initial_contact'>Initial Contact</label>
          <input
            type='text'
            className='form-control'
            id='initial_contact'
            {...register('initial_contact')}
            placeholder='Name of initial contact'
          />
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='representative_user_id'>Sales Rep</label>
          <select
            className='form-control'
            id='representative_user_id'
            {...register('representative_user_id')}
          >
            <option value=''>Select</option>
            {salesReps.map((salesRep) => (
              <option key={salesRep.employee_id} value={salesRep.employee_id}>
                {salesRep.employee_name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='department_id'>Department Called</label>
          <select id='department_id' {...register('department_id')} className='form-control'>
            <option value=''>Select</option>
            {dealSaverInfo.departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.text}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className='col-xs-6 col-lg-3'>
        <div className='form-group' style={{ marginBottom: 15 }}>
          <label htmlFor='call_alert_id'>Call Alert</label>
          <select id='call_alert_id' {...register('call_alert_id')} className='form-control'>
            <option value=''>Select</option>
            {dealSaverInfo.call_alerts.map((callAlert) => (
              <option key={callAlert.id} value={callAlert.id}>
                {callAlert.text}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <label>Caller Information</label>
        <div className='form-horizontal'>
          <div className='form-group' style={{ marginBottom: 15 }}>
            <label htmlFor='caller_email' className='col-xs-2 control-label'>
              Email
            </label>
            <div className='col-xs-10'>
              <input
                type='text'
                className='form-control'
                id='caller_email'
                {...register('caller_email')}
                placeholder="Caller's Email"
              />
            </div>
          </div>

          <div className='form-group' style={{ marginBottom: 15 }}>
            <label htmlFor='caller_alt_phone' className='col-xs-2 control-label'>
              Phone
            </label>
            <div className='col-xs-10'>
              <Controller
                name='caller_alt_phone'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <InputMask
                    mask='(999) 999-9999'
                    type='text'
                    className='form-control mask_phone'
                    id='caller_alt_phone'
                    placeholder="Caller's Phone Number"
                    {...field}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div
          className={`form-group ${formState.errors.appointment_set ? 'has-error' : ''}`}
          style={{ marginBottom: 15 }}
        >
          <label className='control-label'>Appointment Set?</label>
          <div>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='1' />{' '}
              Yes
            </label>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='0' />{' '}
              No
            </label>
            <label className='radio-inline'>
              <input type='radio' {...register('appointment_set', { required: true })} value='-1' />{' '}
              N/A
            </label>
          </div>
          {formState.errors.appointment_set ? (
            <span className='help-block'>Appointment set is required</span>
          ) : null}
        </div>
        {appointmentSet === '1' ? (
          <>
            <div
              className={`form-group ${formState.errors.appointment_date ? 'has-error' : ''}`}
              style={{ marginBottom: 15 }}
            >
              <label htmlFor='appointment_date'>Date</label>
              <input
                className='form-control'
                type='date'
                id='appointment_date'
                {...register('appointment_date', { required: true })}
              />
              {formState.errors.appointment_date ? (
                <span className='help-block'>Appointment date is required</span>
              ) : null}
            </div>
            <div
              className={`form-group ${formState.errors.appointment_time ? 'has-error' : ''}`}
              style={{ marginBottom: 15 }}
            >
              <label htmlFor='appointment_time'>Time</label>
              <input
                className='form-control'
                type='time'
                id='appointment_time'
                {...register('appointment_time', { required: true })}
              />
              {formState.errors.appointment_time ? (
                <span className='help-block'>Appointment time is required</span>
              ) : null}
            </div>
          </>
        ) : null}
        {appointmentSet !== '-1' ? (
          <>
            <div
              className={`form-group ${formState.errors.appointment_offered ? 'has-error' : ''}`}
              style={{ marginBottom: 15 }}
            >
              <label className='control-label'>Appointment Offered</label>
              <div>
                <label className='radio-inline'>
                  <input
                    type='radio'
                    {...register('appointment_offered', { required: true })}
                    value='1'
                  />{' '}
                  Yes
                </label>
                <label className='radio-inline'>
                  <input
                    type='radio'
                    {...register('appointment_offered', { required: true })}
                    value='0'
                  />{' '}
                  No
                </label>
              </div>
            </div>
          </>
        ) : null}
      </div>
      <div className='col-xs-6 col-lg-3'>
        <div
          className={`form-group ${formState.errors.call_summary ? 'has-error' : ''}`}
          style={{ marginBottom: 15 }}
        >
          <label htmlFor='call_summary'>Call Summary</label>
          <textarea
            id='call_summary'
            {...register('call_summary', { required: true })}
            className='form-control'
            rows={6}
            placeholder='A call summary is required.'
          />
          {formState.errors.call_summary ? (
            <span className='help-block'>Call summary is required</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
